import type {
  CaptureSequence,
  CapturedImage,
  CustomLandmarks,
  Landmark,
} from '@/components/posture-analysis/types';
import type {
  AnalysisData,
  PostureClassificationResult,
  PractitionerVisualObservations,
} from './types';
import type { PostureOrientationStrategyId } from './strategies/posture-orientation-strategy-id';
import {
  analyzeAnterior,
  analyzeLateral,
  analyzePosterior,
  toAnalysisDataObject,
} from './posture-pic-evaluation';
import {
  rotateCustomLandmarks,
  rotateLandmarks,
  type FloorCorrectionResult,
} from '../floor-correction';
import { computePostureDiagnoses } from './compute-posture-classification';
import { inferSagittalInputFromCaptured } from './strategies/posture-strategy-context';
import type { SopMetrics } from './strategies/posture-orientation-strategy-types';

export interface EvaluateFullPostureAnalysisOptions {
  captured: Partial<Record<CaptureSequence, CapturedImage | null | undefined>>;
  clientHeightCM?: number;
  unit?: 'cm' | 'in';
  clientSex?: 'Male' | 'Female' | 'Unknown';
  strategyId?: PostureOrientationStrategyId | string | null;
  practitionerObservations?: PractitionerVisualObservations;
  sopCompletionRatio?: number;
  sopTablePosture?: { pattern?: 'APT' | 'SB' | null };
  sopMetrics?: SopMetrics;

}

export interface EvaluateFullPostureAnalysisResult {
  analysis: AnalysisData;
  captured: Partial<Record<CaptureSequence, CapturedImage>>;
  classification: PostureClassificationResult | null;
  effectiveFloorCorrections: Partial<Record<CaptureSequence, FloorCorrectionResult>>;
}

/**
 * Single authoritative entry point for complete posture analysis.
 * Applies manual floor correction when set by the practitioner, otherwise uses
 * vertical plumb (0°). Per-view biomechanical metric extraction and unified
 * classification/orientation across all app contexts.
 *
 * Manual floor correction (floorCorrectionSource === 'manual') is the only
 * correction applied — automatic landmark-based leveling has been removed.
 */
export function evaluateFullPostureAnalysis(
  options: EvaluateFullPostureAnalysisOptions
): EvaluateFullPostureAnalysisResult {
  const {
    captured: rawCaptured,
    unit = 'cm',
    clientSex = 'Unknown',
    strategyId,
    practitionerObservations,
    sopCompletionRatio,
    sopTablePosture,
    sopMetrics,
  } = options;

  const analysis: AnalysisData = {};
  const captured: Partial<Record<CaptureSequence, CapturedImage>> = {};
  const effectiveFloorCorrections: Partial<Record<CaptureSequence, FloorCorrectionResult>> = {};

  const views: CaptureSequence[] = ['Anterior', 'Posterior', 'Left', 'Right'];

  const frontalRows: Partial<Record<'Anterior' | 'Posterior', ReturnType<typeof analyzeAnterior>>> = {};

  for (const view of views) {
    const item = rawCaptured[view];
    if (!item || !item.landmarks || item.landmarks.length === 0) continue;

    const cmPerPx = item.cmPerPx || 0.2;
    const isOptOut = !!item.pelvisPinsOptOut;
    const custom = item.custom;

    // Manual floor correction set by the practitioner takes priority.
    // If no manual correction is present, use vertical plumb (0°).
    const floorCorrection: FloorCorrectionResult =
      item.floorCorrectionSource === 'manual' && typeof item.floorCorrectionDeg === 'number'
        ? {
            correctionDeg: item.floorCorrectionDeg,
            source: 'manual',
            confidence: 1,
            pivot: item.floorCorrectionPivot ?? null,
            warning: item.floorCorrectionWarning,
          }
        : { correctionDeg: 0, source: 'none', confidence: 0, pivot: null };

    effectiveFloorCorrections[view] = floorCorrection;
    const deg = floorCorrection.correctionDeg;
    const pivot = floorCorrection.pivot ?? undefined;

    const rotatedLm = rotateLandmarks(item.landmarks, deg, pivot);
    const rotatedCustom = rotateCustomLandmarks(custom, deg, pivot);

    let rows: ReturnType<typeof analyzeAnterior> = [];
    if (view === 'Anterior') {
      rows = analyzeAnterior(rotatedLm, view, cmPerPx, unit, rotatedCustom);
      frontalRows.Anterior = rows;
    } else if (view === 'Posterior') {
      rows = analyzePosterior(rotatedLm, view, cmPerPx, unit, rotatedCustom);
      frontalRows.Posterior = rows;
    } else if (view === 'Left' || view === 'Right') {
      rows = analyzeLateral(rotatedLm, view, cmPerPx, unit, rotatedCustom, {
        omitPelvicTilt: isOptOut,
      });
      if (rows.length > 0) {
        analysis[view] = toAnalysisDataObject(rows);
      }
    }

    captured[view] = {
      ...item,
      floorCorrectionDeg: deg,
      floorCorrectionSource: floorCorrection.source,
      floorCorrectionConfidence: floorCorrection.confidence,
      floorCorrectionPivot: floorCorrection.pivot,
      floorCorrectionWarning: floorCorrection.warning,
      status: item.status || 'complete',
    };
  }

  if (frontalRows.Anterior?.length) analysis.Anterior = toAnalysisDataObject(frontalRows.Anterior);
  if (frontalRows.Posterior?.length) analysis.Posterior = toAnalysisDataObject(frontalRows.Posterior);

  const { sagittal } = inferSagittalInputFromCaptured(captured);

  const classification = computePostureDiagnoses(analysis, clientSex, {
    strategyId,
    capturedForStrategy: captured,
    strategyInput: {
      ...(sagittal ? { sagittal } : {}),
      ...(sopCompletionRatio !== undefined ? { sopCompletionRatio } : {}),
      ...(sopTablePosture ? { sopTablePosture } : {}),
      ...(practitionerObservations ? { practitionerObservations } : {}),
      ...(sopMetrics ? { sopMetrics } : {}),
    },
  });

  return {
    analysis,
    captured,
    classification,
    effectiveFloorCorrections,
  };
}
